import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Truck, Star, Mail, Phone } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { useVendors } from '@/hooks/use-procurement';
import type { VendorStatus } from '@/lib/types';

export function VendorsListPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<VendorStatus | ''>('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useVendors({
    q: search,
    status,
    page,
    page_size: 20,
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendors</h1>
          <p className="text-gray-600 mt-1">Manage your supplier directory</p>
        </div>
        <Button asChild>
          <Link to="/vendors/new">
            <Plus className="h-4 w-4 mr-2" />
            Add Vendor
          </Link>
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search vendors..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as VendorStatus | '');
            setPage(1);
          }}
          className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : data && data.results.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.results.map((vendor) => (
              <Link key={vendor.id} to={`/vendors/${vendor.id}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Truck className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        {vendor.name}
                      </CardTitle>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant={vendor.status === 'active' ? 'default' : 'secondary'}>
                          {vendor.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                        {vendor.preferred && (
                          <Badge variant="outline" className="text-amber-600 border-amber-600">
                            <Star className="h-3 w-3 mr-1 fill-current" />
                            Preferred
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-gray-600">
                    {vendor.vendor_number && (
                      <p className="text-xs text-gray-500 font-mono">
                        #{vendor.vendor_number}
                      </p>
                    )}
                    {vendor.contact_name && (
                      <p className="font-medium text-gray-700">{vendor.contact_name}</p>
                    )}
                    {vendor.contact_email && (
                      <p className="flex items-center gap-1">
                        <Mail className="h-3.5 w-3.5" />
                        {vendor.contact_email}
                      </p>
                    )}
                    {vendor.contact_phone && (
                      <p className="flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5" />
                        {vendor.contact_phone}
                      </p>
                    )}
                    {vendor.city && vendor.state_province && (
                      <p className="text-gray-500">
                        {vendor.city}, {vendor.state_province}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {data.count > 20 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, data.count)} of {data.count}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!data.previous}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!data.next}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Truck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No vendors found</h3>
            <p className="text-gray-500 mt-1">
              {search || status
                ? 'Try a different search or filter'
                : 'Get started by adding your first vendor'}
            </p>
            {!search && !status && (
              <Button asChild className="mt-4">
                <Link to="/vendors/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vendor
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
